// Brings the element named in the URL hash into view, and keeps it there while the page is still
// filling in. A one-off scroll lands short when content above the target loads afterwards: the
// page is too short to scroll that far yet, then the late content pushes the target down.
//
// Every Section on a page calls this on mount, so one call per URL does the work and the rest
// return. It lets go as soon as the visitor scrolls, clicks or types, or after FOLLOW_MS.
import Router from 'next/router';

const FOLLOW_MS = 8000;
const STOP_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

let followed = {href: null, at: 0};
let listening = false;

// The guard is for the Sections of one page, not for a later visit to the same URL: going Back and
// clicking the same anchor link again within FOLLOW_MS has to scroll.
export const forgetFollowedHash = () => { followed = {href: null, at: 0}; };

const listenForRouteChanges = () => {
    if (listening || !Router?.events?.on) return;
    Router.events.on('routeChangeStart', forgetFollowedHash);
    Router.events.on('hashChangeStart', forgetFollowedHash);
    listening = true;
};

export const followHash = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const raw = window.location.hash.replace('#', '');
    let id;
    // A hand-typed or truncated hash (#50%) is not valid percent-encoding, and decodeURIComponent
    // throws on it. Every Section calls this on mount, so a throw here took the whole page down.
    try {
        id = decodeURIComponent(raw);
    } catch (e) {
        id = raw;
    }
    if (!id) return;
    listenForRouteChanges();
    const {href} = window.location;
    if (followed.href === href && Date.now() - followed.at < FOLLOW_MS) return;
    followed = {href, at: Date.now()};

    const scroll = () => document.getElementById(id)?.scrollIntoView({block: 'start'});
    scroll();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(scroll);
    let timer;
    const stop = () => {
        observer.disconnect();
        clearTimeout(timer);
        STOP_EVENTS.forEach((type) => window.removeEventListener(type, stop, true));
    };
    observer.observe(document.body);
    timer = setTimeout(stop, FOLLOW_MS);
    STOP_EVENTS.forEach((type) => window.addEventListener(type, stop, {capture: true, passive: true}));
};
